/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useState, useEffect } from "react";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { IconButton } from "@/subframe/components/IconButton";
import { ToggleGroup } from "@/subframe/components/ToggleGroup";
import { TextField } from "@/subframe/components/TextField";
import { Select } from "@/subframe/components/Select";
import * as SubframeCore from "@subframe/core";
import { CategoryLabel } from "@/subframe/components/CategoryLabel";
import { Button } from "@/subframe/components/Button";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchLabels } from "../redux/reducers/authSlice";
import { addNotification } from "../redux/reducers/notificationSlice";
import { selectItemPhoneNumber, selectItemDetailsLoading } from "../redux/reducers/itemDetailsSlice";

import axios from "axios";
import monday from "monday-sdk-js";
import { NotificationType, NotificationIcon } from "../Utils/notificationEnums";
import { sendRecipientDetails } from "../Utils/recipientDetailsUtils";

interface NewChatProps {
  isNewChatOpen: boolean;
  setIsNewChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Label {
  id: number;
  label: string;
  user_id: string;
  keywords: string[];
  created_at: number;
}

interface Template {
  id: number;
  user_id: string;
  name: string;
  name_with_details: string;
  category: string;
  type: string;
  allow_category_change: boolean;
  language: string;
  components: {
    name: string;
    category: string;
    language: string;
    components: Array<{
      text?: string;
      type: string;
      format?: string;
      example?: {
        body_text: string[][];
      };
      buttons?: Array<{
        text: string;
        type: string;
        phone_number?: string;
        url?: string;
      }>;
    }>;
  };
  status: string;
  variable_counts: number;
  template_formate: string;
  template_id: string;
}

function ChatSendNewMessage({ isNewChatOpen, setIsNewChatOpen }: NewChatProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { system_user_id, labels, token } = useSelector((state: RootState) => state.auth);
  const phoneNumber = useSelector(selectItemPhoneNumber);
  const detailsLoading = useSelector(selectItemDetailsLoading);

  const [recipientType, setRecipientType] = useState<string>("single");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templateVariables, setTemplateVariables] = useState<{ [key: string]: string }>({});
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState<boolean>(false);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);

  // Form state for single recipient
  const [singleRecipientData, setSingleRecipientData] = useState({
    phoneNumber: "",
    assignedLabels: [] as Label[]
  });

  // Form state for multiple recipients
  const [multipleRecipientsData, setMultipleRecipientsData] = useState({
    selectedLabels: [] as Label[],
    newLabels: [] as Label[]
  });

  // Generate UUID for broadcast identifier
  const generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Parse template format to get variables
  const parseTemplateFormat = (format: string) => {
    if (!format) return [];
    const variables: { type: string, count: number }[] = [];
    const formatParts = format.slice(1, -1).split(',').map(part => part.trim());

    formatParts.forEach(part => {
      const [type, count] = part.split(':');
      variables.push({ type: type.trim(), count: parseInt(count) });
    });

    return variables;
  };

  // Format date to DD/MM/YYYY HH:mm structure
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Fetch labels and templates when component mounts
  useEffect(() => {
    if (system_user_id && isNewChatOpen) {
      dispatch(fetchLabels());
      fetchTemplates();
    }
  }, [system_user_id, isNewChatOpen, dispatch]);

  // Auto-select phone number when phone number is loaded from Redux
  useEffect(() => {
    if (phoneNumber && !singleRecipientData.phoneNumber) {
      handleSingleRecipientChange("phoneNumber", phoneNumber);
    }
  }, [phoneNumber, singleRecipientData.phoneNumber]);

  // Fetch templates from API
  const fetchTemplates = async () => {
    try {
      const response = await axios.get('https://api.insightssystem.com/api:uYdlVwac/web/templates', { headers: { "Content-Type": "application/json", ...(token && { Authorization: token }) } });
      setTemplates(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id.toString() === templateId);
    setSelectedTemplate(template || null);

    if (template) {
      const newVariables: { [key: string]: string } = {};

      // Initialize body variables from the component
      const bodyComponent = template.components.components.find(
        comp => comp.type === 'BODY'
      );
      if (bodyComponent?.example?.body_text?.[0]) {
        bodyComponent.example.body_text[0].forEach((_, index) => {
          newVariables[`b_${index + 1}`] = '';
        });
      }

      // Initialize media variables from header component
      const headerComponent: any = template.components.components.find(
        comp => comp.type === 'HEADER'
      );
      if (headerComponent?.format) {
        const format = headerComponent.format.toLowerCase();
        if (['image', 'video', 'document'].includes(format)) {
          newVariables['m_1'] = '';
        }
      }

      // Initialize website variables from template_formate
      if (template.template_formate) {
        const formatVariables = parseTemplateFormat(template.template_formate);
        formatVariables.forEach(({ type, count }) => {
          switch (type) {
            case 'vw':
              // Only handle up to 2 website variables
              for (let i = 1; i <= Math.min(count, 2); i++) {
                newVariables[`vw_${i}`] = '';
              }
              break;
            case 'm':
              // If media is specified in format and header exists
              if (headerComponent?.format) {
                const format = headerComponent.format.toLowerCase();
                if (['image', 'video', 'document'].includes(format)) {
                  newVariables['m_1'] = '';
                }
              }
              break;
          }
        });
      }

      setTemplateVariables(newVariables);
    } else {
      setTemplateVariables({});
    }
  };

  // Handle template variable changes
  const handleTemplateVariableChange = (key: string, value: string) => {
    setTemplateVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Validate template variables
  const validateTemplateVariables = (): string[] => {
    if (!selectedTemplate) return [];

    const missingFields: string[] = [];

    // Check header component variable if it exists and is required
    const headerComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'HEADER');
    if (headerComponent?.format && !templateVariables.m_1) {
      missingFields.push(`${headerComponent.format} URL`);
    }

    // Check body variables
    const bodyComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BODY');
    bodyComponent?.example?.body_text?.[0]?.forEach((_: any, index: number) => {
      const key = `b_${index + 1}`;
      if (!templateVariables[key]) {
        missingFields.push(`Body Variable {{${index + 1}}}`);
      }
    });

    // Check website variables if they exist in the template format
    const formatVariables = parseTemplateFormat(selectedTemplate.template_formate);
    const hasWebsiteVariables = formatVariables.some(v => v.type === 'vw');
    if (hasWebsiteVariables) {
      formatVariables
        .filter(v => v.type === 'vw')
        .forEach(({ count }) => {
          for (let i = 1; i <= Math.min(count, 2); i++) {
            const key = `vw_${i}`;
            if (!templateVariables[key]) {
              missingFields.push(`Visit Website URL ${i > 1 ? i : ''}`);
            }
          }
        });
    }

    return missingFields;
  };

  // Send message to single recipient
  const handleSendSingleRecipient = async (isScheduled: boolean = false) => {
    if (!selectedTemplate || !singleRecipientData.phoneNumber || !system_user_id) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Missing Information",
          message: "Please select a template and enter a phone number",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    // Validate template variables
    const missingRequiredVariables = validateTemplateVariables();
    if (missingRequiredVariables.length > 0) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Template Validation Failed",
          message: `Please fill in the following required fields: ${missingRequiredVariables.join(', ')}`,
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    try {
      setIsSending(true);

      // Prepare variables for API
      const variables: any = {};
      Object.entries(templateVariables).forEach(([key, value]) => {
        if (key.startsWith('b_')) {
          variables[`body${key.slice(2)}`] = value;
        } else if (key.startsWith('vw_')) {
          const index = parseInt(key.slice(3));
          if (index <= 2) {
            variables[`visit_website${index > 1 ? index : ''}`] = value;
          }
        } else if (key === 'm_1') {
          variables['media'] = value;
        }
      });

      // Prepare payload
      const payload = {
        template: selectedTemplate.template_id,
        variables,
        current_recipient: singleRecipientData.phoneNumber,
        labels: singleRecipientData.assignedLabels.map(label => label.label),
        ...(isScheduled && scheduledTime ? { scheduled_time: formatDate(scheduledTime) } : {})
      };

      // Send to API
      const response = await axios.post('https://api.insightssystem.com/api:uYdlVwac/web/send/new_chat', payload, { headers: { "Content-Type": "application/json", ...(token && { Authorization: token }) } });
      const result = response.data;

      // Check if the API call was successful
      if (!result.success) {
        // Extract error message from WhatsApp response
        const whatsappResponse = result.whatsapp_response_info as any;
        let errorMessage = "Message could not be sent. Please try again.";

        if (whatsappResponse) {
          if (whatsappResponse.error_user_msg) {
            errorMessage = whatsappResponse.error_user_msg;
          } else if (whatsappResponse.error_data?.details) {
            errorMessage = whatsappResponse.error_data.details;
          } else if (whatsappResponse.message) {
            errorMessage = whatsappResponse.message;
          }
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "Message Failed",
            message: errorMessage,
            icon: NotificationIcon.ERROR,
          })
        );
        return;
      }

      // Send recipient details to API
      try {
        await sendRecipientDetails({ phone_number: singleRecipientData.phoneNumber }, token);
      } catch (error) {
        // Log error but don't show notification - this is a non-critical operation
        console.error('Failed to send recipient details:', error);
      }

      void monday().execute("valueCreatedForUser").catch(() => {});

      // Success notification
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.SUCCESS,
          title: "Message Sent",
          message: "Your template message has been sent successfully",
          icon: NotificationIcon.SUCCESS,
        })
      );

      // Clear form data
      setSingleRecipientData({
        phoneNumber: "",
        assignedLabels: []
      });
      setSelectedTemplate(null);
      setTemplateVariables({});

      // Close modal
      setIsNewChatOpen(false);

    } catch (error: any) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Message Not Sent",
          message: error.message || "Something went wrong. Please try again.",
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setIsSending(false);
    }
  };

  // Send broadcast to multiple recipients
  const handleSendMultipleRecipients = async (isScheduled: boolean = false) => {
    if (!selectedTemplate || !system_user_id) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Missing Information",
          message: "Please select a template",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    // Validate template variables
    const missingRequiredVariables = validateTemplateVariables();
    if (missingRequiredVariables.length > 0) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Template Validation Failed",
          message: `Please fill in the following required fields: ${missingRequiredVariables.join(', ')}`,
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    // Validate that at least one label is selected for recipients
    if (multipleRecipientsData.selectedLabels.length === 0) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "No Recipients Selected",
          message: "Please select at least one label to send the message to",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    try {
      setIsSending(true);

      // Prepare variables for API
      const variables: any = {};
      Object.entries(templateVariables).forEach(([key, value]) => {
        if (key.startsWith('b_')) {
          variables[`body${key.slice(2)}`] = value;
        } else if (key.startsWith('vw_')) {
          const index = parseInt(key.slice(3));
          if (index <= 2) {
            variables[`visit_website${index > 1 ? index : ''}`] = value;
          }
        } else if (key === 'm_1') {
          variables['media'] = value;
        }
      });

      // Prepare payload
      const payload = {
        broadcast_identifier: generateUUID(),
        broadcast_name: "label-based-chat",
        schedule: isScheduled && scheduledTime ? formatDate(scheduledTime) : formatDate(new Date(Date.now() + 60000)),
        selected_labels: multipleRecipientsData.selectedLabels.map(label => label.label),
        new_labels: multipleRecipientsData.newLabels.map(label => label.label),
        template: selectedTemplate.template_id,
        variables
      };

      // Send to API
      const response = await axios.post('https://api.insightssystem.com/api:uYdlVwac/web/send/broadcast_schedule', payload, { headers: { "Content-Type": "application/json", ...(token && { Authorization: token }) } });
      const result = response.data;

      // Check if the API call was successful
      if (!result.success) {
        // Extract error message from response
        let errorMessage = "Broadcast could not be sent. Please try again.";

        if (result.message) {
          errorMessage = result.message;
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "Broadcast Failed",
            message: errorMessage,
            icon: NotificationIcon.ERROR,
          })
        );
        return;
      }

      void monday().execute("valueCreatedForUser").catch(() => {});

      // Success notification
      if (isScheduled && scheduledTime) {
        const formattedDateTime = scheduledTime.toLocaleString();
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Broadcast Scheduled",
            message: `Your broadcast will be sent on ${formattedDateTime}`,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Broadcast Sent",
            message: "Your broadcast has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }

      // Clear form data
      setMultipleRecipientsData({
        selectedLabels: [],
        newLabels: []
      });
      setSelectedTemplate(null);
      setTemplateVariables({});
      setScheduledTime(null);

      // Close modal
      setIsNewChatOpen(false);

    } catch (error: any) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Broadcast Not Sent",
          message: error.message || "Something went wrong. Please try again.",
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setIsSending(false);
    }
  };

  // Handle schedule button click
  const handleScheduleClick = () => {
    if (recipientType === "single") {
      // For single recipient, open schedule dialog
      setIsScheduleOpen(true);
    } else {
      // For multiple recipients, open schedule dialog
      setIsScheduleOpen(true);
    }
  };

  // Handle schedule confirmation
  const handleScheduleConfirm = () => {
    if (scheduledTime) {
      if (recipientType === "single") {
        handleSendSingleRecipient(true);
      } else {
        handleSendMultipleRecipients(true);
      }
      setIsScheduleOpen(false);
    }
  };

  // Handle single recipient form changes
  const handleSingleRecipientChange = (field: string, value: string) => {
    setSingleRecipientData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  // Handle label selection for single recipient
  const handleSingleRecipientLabelSelect = (labelId: string) => {
    const selectedLabel = labels.find(label => label.id.toString() === labelId);
    if (selectedLabel && !singleRecipientData.assignedLabels.find(label => label.id === selectedLabel.id)) {
      setSingleRecipientData(prev => ({
        ...prev,
        assignedLabels: [...prev.assignedLabels, selectedLabel]
      }));
    }
  };

  // Handle label removal for single recipient
  const handleSingleRecipientLabelRemove = (labelId: number) => {
    setSingleRecipientData(prev => ({
      ...prev,
      assignedLabels: prev.assignedLabels.filter(label => label.id !== labelId)
    }));
  };

  // Handle label selection for multiple recipients (for selecting recipients by labels)
  const handleMultipleRecipientsLabelSelect = (labelId: string) => {
    const selectedLabel = labels.find(label => label.id.toString() === labelId);
    if (selectedLabel && !multipleRecipientsData.selectedLabels.find(label => label.id === selectedLabel.id)) {
      setMultipleRecipientsData(prev => ({
        ...prev,
        selectedLabels: [...prev.selectedLabels, selectedLabel]
      }));
    }
  };

  // Handle label removal for multiple recipients (for selecting recipients by labels)
  const handleMultipleRecipientsLabelRemove = (labelId: number) => {
    setMultipleRecipientsData(prev => ({
      ...prev,
      selectedLabels: prev.selectedLabels.filter(label => label.id !== labelId)
    }));
  };

  // Handle new label assignment for multiple recipients
  const handleMultipleRecipientsNewLabelSelect = (labelId: string) => {
    const selectedLabel = labels.find(label => label.id.toString() === labelId);
    if (selectedLabel && !multipleRecipientsData.newLabels.find(label => label.id === selectedLabel.id)) {
      setMultipleRecipientsData(prev => ({
        ...prev,
        newLabels: [...prev.newLabels, selectedLabel]
      }));
    }
  };

  // Handle new label removal for multiple recipients
  const handleMultipleRecipientsNewLabelRemove = (labelId: number) => {
    setMultipleRecipientsData(prev => ({
      ...prev,
      newLabels: prev.newLabels.filter(label => label.id !== labelId)
    }));
  };

  // Get available labels for dropdown (filter out already selected ones)
  const getAvailableLabelsForSingle = () => {
    const selectedIds = singleRecipientData.assignedLabels.map(label => label.id);
    return labels.filter(label => !selectedIds.includes(label.id));
  };

  const getAvailableLabelsForMultiple = () => {
    const selectedIds = multipleRecipientsData.selectedLabels.map(label => label.id);
    return labels.filter(label => !selectedIds.includes(label.id));
  };

  const getAvailableLabelsForNewAssignment = () => {
    const selectedIds = multipleRecipientsData.newLabels.map(label => label.id);
    return labels.filter(label => !selectedIds.includes(label.id));
  };

  // Generate template variable fields
  const renderTemplateVariables = () => {
    if (!selectedTemplate) return null;

    const headerComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'HEADER');
    const bodyComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BODY');
    const formatVariables = parseTemplateFormat(selectedTemplate.template_formate);

    return (
      <div className="flex w-full flex-col items-start gap-4">
        {/* Media Variable (Header) */}
        {headerComponent?.format && ['image', 'video', 'document'].includes(headerComponent.format.toLowerCase()) && (
          <TextField
            className="h-auto w-full flex-none"
            label={`${headerComponent.format} Variable (See supported URLs)`}
            helpText={`Insert a supported ${headerComponent.format.toLowerCase()} URL for new message.`}
            icon="FeatherLink"
            iconRight="FeatherLink"
          >
            <TextField.Input
              placeholder={`Enter ${headerComponent.format.toLowerCase()} URL`}
              value={templateVariables.m_1 || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleTemplateVariableChange("m_1", event.target.value)
              }
            />
          </TextField>
        )}

        {/* Body Variables */}
        {bodyComponent?.example?.body_text?.[0]?.map((_: any, index: number) => (
          <TextField
            key={`body_${index + 1}`}
            className="h-auto w-full flex-none"
            label={`Body Variable {{${index + 1}}}`}
            helpText=""
            icon="FeatherCurlyBraces"
            iconRight="FeatherText"
          >
            <TextField.Input
              placeholder={`e.g., ${bodyComponent.example.body_text[0][index] || `Variable ${index + 1}`}`}
              value={templateVariables[`b_${index + 1}`] || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleTemplateVariableChange(`b_${index + 1}`, event.target.value)
              }
            />
          </TextField>
        ))}

        {/* Website Variables */}
        {formatVariables
          .filter(v => v.type === 'vw')
          .map(({ count }) => {
            const maxCount = Math.min(count, 2);
            return Array.from({ length: maxCount }, (_, i) => i + 1).map(index => (
              <TextField
                key={`website_${index}`}
                className="h-auto w-full flex-none"
                label={`Button Variable {{${index}}}`}
                helpText="Insert the dynamic URL for your 'Visit Website' button here."
                icon="FeatherCurlyBraces"
                iconRight="FeatherText"
              >
                <TextField.Input
                  placeholder={`e.g., https://example.com`}
                  value={templateVariables[`vw_${index}`] || ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleTemplateVariableChange(`vw_${index}`, event.target.value)
                  }
                />
              </TextField>
            ));
          })}
      </div>
    );
  };

  // Render header media preview
  const renderHeaderMedia = (component: any) => {
    if (!component?.format) return null;

    switch (component.format.toLowerCase()) {
      case 'image':
        return (
          <div className="h-48 w-full flex-none rounded-t-lg bg-neutral-100 flex flex-col items-center justify-center">
            <SubframeCore.Icon
              className="text-neutral-400"
              name="FeatherImage"
            />
            <span className="mt-2 text-sm text-neutral-500">Image Preview</span>
          </div>
        );
      case 'video':
        return (
          <div className="h-48 w-full flex-none rounded-t-lg bg-neutral-100 flex flex-col items-center justify-center">
            <SubframeCore.Icon
              className="text-neutral-400"
              name="FeatherVideo"
            />
            <span className="mt-2 text-sm text-neutral-500">Video Preview</span>
          </div>
        );
      case 'document':
        return (
          <div className="h-48 w-full flex-none rounded-t-lg bg-neutral-100 flex flex-col items-center justify-center">
            <SubframeCore.Icon
              className="text-neutral-400"
              name="FeatherFileText"
            />
            <span className="mt-2 text-sm text-neutral-500">Document Preview</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Generate template preview
  const renderTemplatePreview = () => {
    if (!selectedTemplate) {
      return (
        <div className="flex max-w-[240px] flex-col items-start rounded-lg border border-solid border-neutral-border bg-neutral-100 shadow-md">
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 px-4 pt-4 pb-0.5">
            <span className="font-['Nunito'] text-[13px] font-[600] leading-[20px] text-default-font">
              Select a template to see the preview
            </span>
          </div>
        </div>
      );
    }

    const headerComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'HEADER');
    const bodyComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BODY');
    const buttonComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BUTTONS');

    let previewText = bodyComponent?.text || "Template preview will appear here";

    // Replace variables in preview text
    Object.entries(templateVariables).forEach(([key, value]) => {
      if (key.startsWith('b_')) {
        const index = parseInt(key.slice(2));
        const placeholder = `{{${index}}}`;
        previewText = previewText.replace(placeholder, value || placeholder);
      }
    });

    // Format text with proper direction and line breaks
    const formatText = (text: string) => {
      if (!text) return '';
      const rtlLanguages = ['ar', 'he', 'ur', 'fa'];
      const isRTL = rtlLanguages.includes(selectedTemplate?.language?.toLowerCase());

      return (
        <span
          className={`whitespace-pre-wrap w-full text-xs leading-relaxed text-default-font ${isRTL ? "text-right" : "text-left"}`}
          dir={isRTL ? 'rtl' : 'ltr'}
          dangerouslySetInnerHTML={{
            __html: text.split('\n').join('<br />')
          }}
        />
      );
    };

    return (
      <div className="flex max-w-[240px] flex-col items-start rounded-lg border border-solid border-neutral-border bg-neutral-100 shadow-md">
        {renderHeaderMedia(headerComponent)}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 px-4 pt-4 pb-0.5 text-default-font">
          {formatText(previewText)}
        </div>
        <div className="flex w-full flex-col items-start">
          {buttonComponent?.buttons?.map((button: any, index: any) => (
            <div key={index} className="flex w-full flex-col items-center justify-center gap-2 border-t-2 border-solid border-neutral-border px-3 py-3">
              <div className="flex items-center gap-2">
                <SubframeCore.Icon
                  className="text-heading-3 font-heading-3 text-[#10b989ff]"
                  name={
                    button.type === 'QUICK_REPLY' ? 'FeatherReply' :
                      button.type === 'URL' ? 'FeatherArrowUpRightSquare' :
                        button.type === 'PHONE_NUMBER' ? 'FeatherPhone' :
                          button.type === 'COPY_CODE' ? 'FeatherCopy' : 'FeatherList'
                  }
                />
                <span className="font-['Nunito'] text-xs font-[600] leading-[20px] text-success-500">
                  {button.text}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <DialogLayout open={isNewChatOpen} onOpenChange={setIsNewChatOpen}>
        <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-2 py-2">
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 bg-default-background px-4 py-4">
            <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Send Message
                </span>
                <span className="text-body font-body text-subtext-color">
                  Choose recipient type and compose your message
                </span>
              </div>
              <IconButton
                icon="FeatherX"
                onClick={() => setIsNewChatOpen(false)}
              />
            </div>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full items-center justify-center">
                <ToggleGroup
                  value={recipientType}
                  onValueChange={(value: string) => {
                    // Only update if a valid value is provided and it's different
                    if (value && (value === "single" || value === "multiple")) {
                      setRecipientType(value);
                    }
                  }}
                >
                  <ToggleGroup.Item icon="FeatherUser" value="single">
                    Single Recipient
                  </ToggleGroup.Item>
                  <ToggleGroup.Item icon="FeatherUsers" value="multiple">
                    Broadcast
                  </ToggleGroup.Item>
                </ToggleGroup>
              </div>

              {/* Single Recipient Form */}
              {recipientType === "single" && (
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
                    {/* Show phone number if loaded, otherwise show manual input */}
                    {phoneNumber ? (
                      <TextField
                        className="h-auto w-full flex-none"
                        label="Phone Number"
                        helpText="Phone number loaded from item details"
                        icon="FeatherPhone"
                      >
                        <TextField.Input
                          placeholder="Phone number"
                          value={singleRecipientData.phoneNumber || phoneNumber}
                          disabled={true}
                          readOnly={true}
                        />
                      </TextField>
                    ) : (
                      <>
                        {/* Loading state message */}
                        {detailsLoading && (
                          <div className="flex items-center gap-2 text-sm text-subtext-color">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            Loading phone number from item details...
                          </div>
                        )}
                        {/* Fallback text input when no phone number available */}
                        {!detailsLoading && (
                          <TextField
                            className="h-auto w-full flex-none"
                            label="Phone Number (Manual Entry)"
                            helpText="Include country code (e.g. +1)"
                            icon="FeatherPhone"
                          >
                            <TextField.Input
                              placeholder="Enter phone number manually"
                              value={singleRecipientData.phoneNumber}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                handleSingleRecipientChange("phoneNumber", event.target.value)
                              }
                            />
                          </TextField>
                        )}
                      </>
                    )}
                    <div className="flex w-full flex-col items-start gap-4">
                      <Select
                        className="h-auto w-full flex-none"
                        label="Select Template"
                        placeholder="Choose a template"
                        helpText="Select a template to use for your message"
                        icon="FeatherLayoutTemplate"
                        value={selectedTemplate?.id.toString() || ""}
                        onValueChange={(value: string) => handleTemplateSelect(value)}
                      >
                        {templates.map((template) => (
                          <Select.Item key={template.id} value={template.id.toString()}>
                            {template.name}
                          </Select.Item>
                        ))}
                      </Select>
                      {renderTemplateVariables()}
                    </div>
                    <Select
                      className="h-auto w-full flex-none"
                      label="Assign Labels (Optional)"
                      placeholder="Choose Label(s)"
                      helpText="Assign optional labels for recipient"
                      icon="FeatherTag"
                      value={undefined}
                      onValueChange={(value: string) => handleSingleRecipientLabelSelect(value)}
                    >
                      {getAvailableLabelsForSingle().map((label) => (
                        <Select.Item key={label.id} value={label.id.toString()}>
                          {label.label}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <div className="flex flex-col items-start gap-6">
                    <div className="flex flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Template Preview
                      </span>
                      {renderTemplatePreview()}
                    </div>
                    {singleRecipientData.assignedLabels.length > 0 && (
                      <div className="flex flex-col items-start gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Assigned Labels
                        </span>
                        <div className="w-60 h-48 border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex flex-wrap gap-2 h-full overflow-y-auto">
                            {singleRecipientData.assignedLabels.map((label) => (
                              <CategoryLabel
                                key={label.id}
                                className="h-6 w-auto flex-none"
                                text={label.label}
                                onClick={() => handleSingleRecipientLabelRemove(label.id)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Multiple Recipients Form */}
              {recipientType === "multiple" && (
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">

                    <div className="flex flex-col items-start gap-2">
                      <Select
                        label="Select Labels"
                        placeholder="Choose Label(s) of recipients"
                        helpText="Select the Label(s) of recipients you want to send the message to"
                        icon="FeatherTag"
                        value={undefined}
                        onValueChange={(value: string) => handleMultipleRecipientsLabelSelect(value)}
                      >
                        {getAvailableLabelsForMultiple().map((label) => (
                          <Select.Item key={label.id} value={label.id.toString()}>
                            {label.label}
                          </Select.Item>
                        ))}
                      </Select>
                      {multipleRecipientsData.selectedLabels.length > 0 && (
                        <div className="w-96 h-48 border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex flex-wrap gap-2 h-full overflow-y-auto">
                            {multipleRecipientsData.selectedLabels.map((label) => (
                              <CategoryLabel
                                key={label.id}
                                className="h-6 w-auto flex-none"
                                text={label.label}
                                onClick={() => handleMultipleRecipientsLabelRemove(label.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <Select
                      className="h-auto w-full flex-none"
                      label="Assign New Labels (Optional)"
                      placeholder="Choose New Label(s)"
                      helpText="Assign optional new labels for recipient"
                      icon="FeatherTag"
                      value={undefined}
                      onValueChange={(value: string) => handleMultipleRecipientsNewLabelSelect(value)}
                    >
                      {getAvailableLabelsForNewAssignment().map((label) => (
                        <Select.Item key={label.id} value={label.id.toString()}>
                          {label.label}
                        </Select.Item>
                      ))}
                    </Select>
                    <div className="flex w-full flex-col items-start gap-4">
                      <Select
                        className="h-auto w-full flex-none"
                        label="Select Template"
                        placeholder="Choose a template"
                        helpText="Select a template to use for your message"
                        icon="FeatherLayoutTemplate"
                        value={selectedTemplate?.id.toString() || ""}
                        onValueChange={(value: string) => handleTemplateSelect(value)}
                      >
                        {templates.map((template) => (
                          <Select.Item key={template.id} value={template.id.toString()}>
                            {template.name}
                          </Select.Item>
                        ))}
                      </Select>
                      {renderTemplateVariables()}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-6">
                    <div className="flex flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Template Preview
                      </span>
                      {renderTemplatePreview()}
                    </div>

                    {multipleRecipientsData.newLabels.length > 0 && (
                      <div className="flex flex-col items-start gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Newly Assigned Labels
                        </span>
                        <div className="w-60 h-48 border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex flex-wrap gap-2 h-full overflow-y-auto">
                            {multipleRecipientsData.newLabels.map((label) => (
                              <CategoryLabel
                                key={label.id}
                                className="h-6 w-auto flex-none"
                                text={label.label}
                                onClick={() => handleMultipleRecipientsNewLabelRemove(label.id)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex w-full items-center gap-4">
                <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2">
                  <Button
                    variant="neutral-secondary"
                    icon="FeatherClock"
                    disabled={isSending}
                    onClick={handleScheduleClick}
                  >
                    Schedule
                  </Button>
                  <Button
                    icon="FeatherSend"
                    disabled={isSending}
                    onClick={recipientType === "single" ? () => handleSendSingleRecipient(false) : () => handleSendMultipleRecipients(false)}
                  >
                    {isSending ? "Sending..." : "Send Now"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogLayout>

      {/* Schedule Dialog for Multiple Recipients */}
      {isScheduleOpen && (
        <DialogLayout open={isScheduleOpen} onOpenChange={setIsScheduleOpen}>
          <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-2 py-2">
            <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 bg-default-background px-4 py-4">
              <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {recipientType === "single" ? "Schedule Message" : "Schedule Broadcast"}
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    Choose when to send your {recipientType === "single" ? "message" : "broadcast message"}
                  </span>
                </div>
                <IconButton
                  icon="FeatherX"
                  onClick={() => setIsScheduleOpen(false)}
                />
              </div>
              <div className="flex w-full flex-col items-start gap-4">
                <TextField
                  className="h-auto w-full flex-none"
                  label="Schedule Date & Time"
                  helpText="Select when to send the broadcast"
                  icon="FeatherCalendar"
                >
                  <TextField.Input
                    type="datetime-local"
                    value={scheduledTime ? scheduledTime.toISOString().slice(0, 16) : ""}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const value = event.target.value;
                      if (value) {
                        setScheduledTime(new Date(value));
                      } else {
                        setScheduledTime(null);
                      }
                    }}
                  />
                </TextField>
                <div className="flex w-full items-center gap-4">
                  <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2">
                    <Button
                      variant="neutral-secondary"
                      onClick={() => setIsScheduleOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={!scheduledTime || isSending}
                      onClick={handleScheduleConfirm}
                    >
                      {isSending ? "Scheduling..." : `Schedule ${recipientType === "single" ? "Message" : "Broadcast"}`}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogLayout>
      )}
    </>
  );
}

export default ChatSendNewMessage;